import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {BlockBaseComponent} from "../scene-blocks/block-base/block-base.component";
import {MatDialog} from '@angular/material/dialog';
import {animate, style, transition, trigger} from '@angular/animations';
import {DialogPopComponent} from "../../../components/dialog-pop/dialog-pop.component";
import {IAppSettingsWrap, Id, IPage, IPlayerProps, IResource, IResourcePreview, ISceneBlock} from "../../../interfaces";
import {actionAddSceneItem, actionCRUDScene, actionRemoveSceneBlocks, actionSceneItemPlaylist, actionSceneJumpLinked, actionSelectedSceneItemId} from "../../../store/actions/user.actions";
import {BASIC_COLORS, IBlockLabelsRecords, requiresToolboxContent} from "../scene-blocks/block-constants";
import {DialogHotkeysComponent} from '../../../components/dialog-hotkeys/dialog-hotkeys.component';
import {ADDBLOCK_OFFSET, compareIds, convertBooleanToNumbers, convertNumberToBoolean, createPageData, getGroupChildren, getPlayerIdInScene, getResourcePreview, isGroupItem, isNullOrUndefOrEmpty, reorderPages, roundNumber, setGroupDesignBox, TIMELINE_GRID_H2, TIMELINE_SCALE_LONG, TIMELINE_ZOOMS} from "../../../common";
import {TranslocoService} from "@ngneat/transloco";
import {ContainerService, LocalStoreService, SceneService, StoreSelectorsService} from "../../../services";
import {TableDialogService} from "../../../services/table-dialog.service";
import {ITimelineViewMode} from "../scene-editor/scene-editor.component";
import {Router} from "@angular/router";
import {ToastrService} from 'ngx-toastr';
import {filter, skipWhile, take} from 'rxjs/operators';
import {isNullOrUndefined} from 'util';
import {MatSelectChange} from '@angular/material/select';
import * as _ from 'lodash';

type ChangeType = 'Resize' | 'Move' | 'Align';

interface IPlaylistItem {
    type: string;
    originalData: any;
    channel: number;
    duration: number;
    selected: boolean;
    title: string;
    start: number;
    pageType: string;
    pageSrc?: string;
    resource: any;
    pageId: number;
}

interface IPlaylistChannel {
    id: number;
    playlistId: string;
    name: string;
    label: string;
    player: any;
    originalData: any;
    color: string;
    type: string;
    selected: boolean;
    top: number;
    pageIndexes: Array<number>;
    duration: number;
    defaultDuration?: number;
}

interface IPlaylistState {
    duration: number;
    gridWidth: number;
    items: IPlaylistItem[];
    channels: IPlaylistChannel[];
    frozen: boolean;
    magnetic: boolean;
    zoom: number;
}

interface IPlaylistOption {
    name: string;
    id: string;
}

@Component({
    selector: 'app-playlist-editor',
    templateUrl: './playlist-editor.component.html',
    styleUrls: ['./playlist-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [
            transition('* => *', [
                style({ opacity: 0 }),
                animate('1500ms ease-in-out')
            ])
        ])
    ],
    standalone: false
})
export class PlaylistEditorComponent extends BlockBaseComponent implements OnInit, AfterViewInit {
    readonly zoomLength = TIMELINE_ZOOMS.length;
    readonly rulerH = 53;
    readonly rulerOffsetX = 46;

    state: IPlaylistState = {
        duration: 3600,
        gridWidth: 36000,
        items: [],
        channels: [],
        frozen: false,
        magnetic: false,
        zoom: 1
    };

    sceneBlocks: ISceneBlock[];
    playlists: IPlaylistChannel[] = [];
    pages: IPlaylistItem[] = [];
    selectedPlayers: IPlaylistItem[] = [];
    copiedPlayers: IPlaylistItem[] = [];

    playlistSelection: IPlaylistOption[] = [];
    playlistOptions = [];
    timelineTotalDuration = 0;
    activeChannelPlaylistId;
    activeChannelPlaylist: ISceneBlock;

    zoom = 1;
    zoomIndex = TIMELINE_ZOOMS.indexOf(this.zoom);
    selectedIndexes = [];
    disableUndo = true;
    disableRedo = true;
    m_mode: ITimelineViewMode;
    storePath: string;
    path: 'Collection' | 'Playlist';
    touchPlayerIndex = -1;
    coreAppData: IAppSettingsWrap;
    resourceList = {};
    resources: IResource[] = [];
    scenePreviewEnabled = true;
    selectedPlaylistId;
    enableUndoRedo = true;
    playlistBounds;
    dragPreviewEle;
    dndData: any;
    // full current scene tree - used to resolve a NESTED playlist (a Playlist embedded as a page inside
    // another playlist) that listenPlayersOfScene (top-level only) doesn't return
    private currentSceneData: IPlayerProps;

    @Input() limit = 10;
    @Input() propsMinimized = false;
    @Input() miniView = false;

    @Input()
    set mode(i_mode: ITimelineViewMode) {
        this.m_mode = i_mode;
        switch (this.m_mode) {
            case "collectionMode": {
                this.storePath = `Data[0].Collection[0]`;
                this.path = "Collection";
                break;
            }
            case "playlistMode": {
                this.storePath = `Data[0].Playlist[0]`;
                this.path = 'Playlist';
                break;
            }
        }
    }

    @Output() toggleMinimize: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        storeSelector: StoreSelectorsService,
        private sceneService: SceneService,
        private containerService: ContainerService,
        private translocoService: TranslocoService,
        public override dialog: MatDialog,
        private toastr: ToastrService,
        private cd: ChangeDetectorRef,
        private router: Router,
        protected override localStoreService: LocalStoreService,
        private tableDialogService: TableDialogService
    ) {
        super(storeSelector, dialog);

        this.scenePreviewEnabled = convertNumberToBoolean(this.localStoreService.getStorageValConfig('playlistScenePreviewEnabled'));
        const z = this.localStoreService.getStorageValConfig('timelineZoom');
        if (z) {
            const ind = TIMELINE_ZOOMS.indexOf(+z);
            if (ind > -1) {
                this.zoom = z;
                this.zoomIndex = ind;
            }
        }
    }

    override componentInit(): void {
        super.componentInit();

        this.cod(
            this.storeSelector.getCoreData()
                .subscribe((i_coreData: IAppSettingsWrap) => this.coreAppData = i_coreData)
        );
        this.cod(
            this.storeSelector.getResources().subscribe((res) => {
                this.resources = res || [];
            })
        );
        this.cod(
            this.storeSelector.listenSelectedScene().subscribe((scene) => {
                if (scene) this.currentSceneData = scene;
            })
        );
        this.cod(
            this.storeSelector.listenPlayersOfScene(this.sceneId).subscribe((data) => {
                if (!data) return;
                this.sceneBlocks = this.collectPlaylistBlocks(data);
                this.createPlaylistOptions();
                this.render();
            })
        );
        this.cod(
            this.storeSelector.listenSelectedScenePlaylist().subscribe((v) => {
                if (!v) this.selectedIndexes = [];
                else this.selectedIndexes = v;
                this.setSelectedPlayers();
                this.cd.detectChanges();
            })
        );
        this.cod(
            this.storeSelector.getPlayerIndexByTouch(this.sceneId).subscribe(v => {
                this.touchPlayerIndex = v;
            })
        );
        this.cod(
            this.containerService.listenDndContent()
                .pipe(
                    filter((data) => {
                        if (data?.item?.blockType === 'mp3') {
                            this.removeDragPreview();
                            return false;
                        }
                        if (!data || data.component != 'playlist') {
                            this.removeDragPreview();
                            return false;
                        }
                        return true;
                    }),
                    skipWhile((v) => {
                        return JSON.stringify(this.dndData) === JSON.stringify(v);
                    })
                    // distinctUntilChanged((prev, curr) => {
                    //     return prev.dropPointX === curr.dropPointX &&
                    //         prev.isDragging === curr.isDragging &&
                    //         prev.isDrop === curr.isDrop;
                    // })
                )
                .subscribe((data) => {
                    this.dndData = data;

                    if (data.isDragging || data.isDrop) {
                        this.dndItemToPlaylist(data);
                    } else {
                        this.addItemToPlaylist(data.item);
                    }

                    if (!data.isDragging) {
                        this.containerService.setDndContent(null);
                    }
                })
        );

        if (this.enableUndoRedo) {
            this.cod(
                this.storeSelector.getUndoable().subscribe((status: boolean) => {
                    this.disableUndo = status;
                })
            );
            this.cod(
                this.storeSelector.getRedoable().subscribe((status: boolean) => {
                    this.disableRedo = status;
                })
            );
            this.storeSelector.dispatch({type: 'BREAK_MERGE'});
        }
    }

    override componentStoreUpdates() {
        if (this.sceneItemSwitched) {
            setTimeout(() => {
                this.render();
            }, 100);
        }
        super.componentStoreUpdates();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.dragPreviewEle = document.querySelector(".drag-preview-item");
        }, 3000); // fade-in animation takes 1.5s
    }

    render() {
        this.selectedPlaylistId = this.sceneItemId;
        this.activeChannelPlaylistId = this.selectedPlaylistId;
        this.playlists = [];
        this.pages = [];

        this.setPlaylistSelection();
        this.buildPlaylistsTimeline();
        this.setSelectedPlayers();

        setTimeout(() => {
            this.removeDragPreview();
            this.cd.detectChanges();
        });
    }

    // a Playlist/Collection can sit at the top of the scene, inside a Group, OR inside a Collection/Playlist
    // page (e.g. the mouse-interrupt wrapper Collection whose first page embeds the timeline Playlist). A
    // group keeps its children in its own <Group> node (Data[0].Group[0].Players[0].Player) - NOT <Scene> -
    // so getGroupChildren() is the ONLY correct way to reach them (see functions.ts getGroupNode). A
    // Collection/Playlist keeps its page players under Data[0].{Collection|Playlist}[0].Page[].Player[0].
    // listenPlayersOfScene returns top-level players only, so BOTH nestings are walked (mirrors
    // findSceneItemDeep) - without it a playlist inside a group/collection page has no channel: no timeline,
    // and nothing to add items to.
    private collectPlaylistBlocks(players: ISceneBlock[]): ISceneBlock[] {
        const targetId = this.m_mode === "collectionMode"
            ? this.blockLabelRecords.COLLECTION.id
            : this.blockLabelRecords.PLAYLIST.id;
        const blocks: ISceneBlock[] = [];
        const walk = (i_players: ISceneBlock[]) => {
            (i_players || []).forEach((block) => {
                if (compareIds(block?.$?.player, targetId)) {
                    blocks.push(block);
                }
                // descend into Group children AND Collection/Playlist page players (a matched block may itself
                // still contain a nested target, so this is not an else-branch).
                const nested: ISceneBlock[] = [
                    ...(getGroupChildren(block) || []),
                    ...[...(block?.Data?.[0]?.Playlist?.[0]?.Page || []), ...(block?.Data?.[0]?.Collection?.[0]?.Page || [])]
                        .map((page) => page?.Player?.[0])
                        .filter((player) => !!player),
                ] as ISceneBlock[];
                if (nested.length) {
                    walk(nested);
                }
            });
        };
        walk(players);
        return blocks;
    }

    private createPlaylistOptions() {
        this.playlistOptions = this.sceneBlocks.map((i_playerListBlock) => ({
            name: i_playerListBlock.$.label,
            id: i_playerListBlock.$.id
        }));
    }

    private setPlaylistSelection() {
        if (!this.playlistSelection.length) {
            this.playlistSelection = this.playlistOptions.filter((v) => v.id === this.selectedPlaylistId);
        } else {
            const selections = _.cloneDeep(this.playlistSelection);
            this.playlistSelection = this.playlistOptions.filter((v) => {
                return selections.some((opt) => compareIds(opt.id, v.id));
            });
        }
    }

    private getSelectedPlaylists() {
        if (this.miniView) {
            // the selected playlist may be a TOP-LEVEL block or a NESTED one (a Playlist embedded as a page
            // inside another playlist). Try top-level first, then a deep lookup by id so drilling into a
            // nested playlist shows ITS timeline (empty for a freshly added one), not the parent's.
            const topLevel = this.sceneBlocks.filter((v) => compareIds(this.selectedPlaylistId, v.$.id));
            if (topLevel.length) return topLevel;
            const nested = this.findPlaylistDeep(this.selectedPlaylistId);
            return nested ? [nested] : [];
        }
        if (this.playlistSelection.length === 0) {
            return [];
        }
        return this.sceneBlocks.filter((v) => this.playlistSelection.some((opt) => compareIds(opt.id, v.$.id)));
    }

    // find a playlist block anywhere in the current scene tree (handles a nested Playlist component)
    private findPlaylistDeep(playlistId): ISceneBlock {
        if (!playlistId || !this.currentSceneData) return null;
        try {
            const found = getPlayerIdInScene([this.currentSceneData], this.sceneId, playlistId)?.sceneItem;
            if (found && compareIds(found.$.player, this.blockLabelRecords.PLAYLIST.id)) {
                return found as ISceneBlock;
            }
        } catch {
            // getPlayerIdInScene throws when the id isn't in the tree - treat as not found
        }
        return null;
    }

    private buildPlaylistsTimeline() {
        let longestTimeline;
        let pageIndex = 0;
        const selectedPlaylists = this.getSelectedPlaylists();
        if (isNullOrUndefOrEmpty(selectedPlaylists) || selectedPlaylists.length === 0) {
            return;
        }

        selectedPlaylists.forEach((i_playerlist, counter) => {
            let timeOffset: any = 0;
            this.timelineTotalDuration = 0;
            const defaultDuration = Number(i_playerlist.Data[0][this.path][0].$.duration);
            const pageIndexes = [];
            let storePages = i_playerlist.Data[0][this.path][0].Page;

            if (isNullOrUndefOrEmpty(storePages)) {
                this.dispatchSceneItemChanges({
                    sceneId: this.sceneId,
                    sceneItemId: i_playerlist.$.id,
                    values: [{
                        key: 'Page',
                        value: [],
                        storePath: this.storePath
                    }]
                }, true);
                storePages = [];
                return;
            }
            // fix of the crash reported 8-28-2023
            if (!storePages?.forEach) return;
            storePages.forEach((i_page: IPage) => {
                let pageDuration: any = i_page.$.duration ? i_page.$.duration : defaultDuration;
                pageDuration = Number(pageDuration);
                const pageType = i_page.$.type;

                // add channel items
                this.pages.push({
                    type: "channel",
                    originalData: i_page,
                    channel: counter,
                    duration: pageDuration,
                    selected: false,
                    title: i_page.$.page,
                    start: timeOffset,
                    pageType,
                    resource: {
                        type: "fa",
                        src: pageType === 'scene' ? 'control_camera' : (
                            pageType === 'component' ? 'widgets' : (
                                compareIds(i_page.Player[0]?.$.player, '3100') ? 'play_circle_outline' : 'image'
                            )
                        )
                    },
                    // a "component" page embeds its player directly (no Resource) - don't resolve a resource url
                    pageSrc: pageType === 'scene' ? i_page.Player[0]?.$.src : (
                        pageType === 'component' ? null : this.getResourceUrl(i_page.Player[0]?.Data[0]?.Resource[0]?.$?.resource)
                    ),
                    pageId: pageIndex
                });
                pageIndexes.push(pageIndex);

                // calc duration and time offset
                timeOffset = timeOffset + pageDuration;
                this.timelineTotalDuration = this.timelineTotalDuration + pageDuration;
                longestTimeline = !longestTimeline ? this.timelineTotalDuration : (this.timelineTotalDuration > longestTimeline ? this.timelineTotalDuration : longestTimeline);
                pageIndex++;
            });

            /** Playlists **/
            this.playlists.push({
                id: counter,
                playlistId: i_playerlist.$.id,
                name: '',
                label: i_playerlist.$.label,
                player: i_playerlist.$.player,
                originalData: i_playerlist,
                color: BASIC_COLORS[counter % 10],
                type: "common",
                selected: i_playerlist.$.id === this.activeChannelPlaylistId ? true : false,
                top: 0,
                pageIndexes,
                duration: timeOffset,
                defaultDuration
            });
        });
        // this.buildPages(this.pages);
        this.setState(longestTimeline);
    }

    private setState(i_longestTimeline?: number) {
        this.state = {
            duration: i_longestTimeline ? i_longestTimeline : this.timelineTotalDuration,
            gridWidth: i_longestTimeline ? i_longestTimeline : this.timelineTotalDuration * 100,
            items: this.pages,
            channels: this.playlists,
            frozen: false,
            magnetic: false,
            zoom: this.zoom
        };
    }

    private setStorePages(changeType: ChangeType = null) {
        const timelineState = this.state;
        if (!timelineState) return;
        const pages = reorderPages(timelineState.items, timelineState.channels);

        Object.values(pages).forEach((i_playlist) => {
            if ('id' in i_playlist) {
                const playlistId = i_playlist.playlistId;

                // if items are resized or aligned, update items of only selected channel
                if ((changeType === 'Resize' || changeType === 'Align') && playlistId != this.activeChannelPlaylistId) {
                    return;
                }

                const updatedPages = [];
                const selectedIndexes = [];
                const currentPages: Array<any> = _.cloneDeep(pages[`pages_${i_playlist.id}`]);
                Object.values(currentPages).forEach((i_page: IPlaylistItem, index) => {
                    const pageUpdated = createPageData(i_page, this.storeSelector);
                    if (changeType === 'Resize' && i_page.selected) {
                        pageUpdated.$.duration = i_page.duration + '';
                    }
                    if (changeType === 'Move' && i_page.selected) {
                        selectedIndexes.push(index);
                    }
                    updatedPages.push(pageUpdated);
                });

                this.dispatchSceneItemChanges({
                    sceneId: this.sceneId,
                    sceneItemId: playlistId,
                    values: [{
                        key: 'Page',
                        value: updatedPages,
                        storePath: this.storePath
                    }]
                }, true);

                if (playlistId == this.activeChannelPlaylistId) {
                    this.storeSelector.dispatch(actionSceneItemPlaylist({indexes: selectedIndexes}));
                }
            }
        });
        if (this.enableUndoRedo) {
            this.storeSelector.dispatch({type: 'BREAK_MERGE'});
        }
    }

    private removePlayers() {
        this.setSelectedPlayers();
        const removePageIds = [];
        this.selectedPlayers.forEach((player) => {
            removePageIds.push(player.pageId);
        });
        if (removePageIds.length == 0) {
            return;
        }

        const timelineState = this.state;
        if (!timelineState) return;
        const pages = reorderPages(timelineState.items, timelineState.channels);
        Object.values(pages).forEach((i_playlist) => {
            if ('id' in i_playlist) {
                const updatedPages = [];
                const currentPages: Array<any> = pages[`pages_${i_playlist.id}`];
                Object.values(currentPages).forEach((i_page) => {
                    if (removePageIds.indexOf(i_page.pageId) == -1) {
                        const pageUpdated = createPageData(i_page, this.storeSelector);
                        updatedPages.push(pageUpdated);
                    }
                });
                this.dispatchSceneItemChanges({
                    sceneId: this.sceneId,
                    sceneItemId: i_playlist.playlistId,
                    values: [{
                        key: 'Page',
                        value: updatedPages,
                        storePath: this.storePath
                    }]
                }, true);
            }
        });
        this.selectedPlayers = [];
        this.resetSelection();
        if (this.enableUndoRedo) {
            this.storeSelector.dispatch({type: 'BREAK_MERGE'});
        }
    }

    onChangeDropdown(event: MatSelectChange) {
        this.playlistSelection = event.value;
        this.storeSelector.dispatch(actionSceneItemPlaylist({indexes: []}));
        this.storeSelector.dispatch(actionSelectedSceneItemId({id: event.value.length > 0 ? event.value[0].id : this.sceneId}));
        this.render();
    }

    addContent() {
        this.sceneService.openPlaylistAssociateDialog(this.sceneItem, null);
    }

    addNewPlaylist() {
        // generate playlist block
        let blockData: ISceneBlock;
        switch (this.m_mode) {
            case "collectionMode": {
                blockData = _.clone(this.blockLabelRecords.COLLECTION.template);
                break;
            }
            case "playlistMode": {
                blockData = _.clone(this.blockLabelRecords.PLAYLIST.template);
                break;
            }
        }

        blockData.$.id = _.uniqueId('__');
        const offset = this.storeSelector.addedBlockCount * ADDBLOCK_OFFSET;
        this.storeSelector.addedBlockCount += 1;
        blockData.Data[0].Layout[0].$.x = String(offset);
        blockData.Data[0].Layout[0].$.y = String(offset);

        // update playlist selection
        const newSelection = {
            name: blockData.$.label,
            id: blockData.$.id
        };
        this.playlistOptions.push(newSelection);
        this.playlistSelection.push(newSelection);

        // update store
        this.storeSelector.dispatch(actionAddSceneItem({
            sceneId: this.sceneId,
            sceneItemId: this.sceneId,
            updatedItemIds: [blockData.$.id],
            values: [{
                key: 'Player',
                cloneValue: true,
                value: blockData,
                storePath: 'Data["0"].Scene["0"].Players[0]',
                addIndex: this.touchPlayerIndex < 0 ? null : this.touchPlayerIndex
            }]
        }));
    }

    /* Toolbar action: remove playlist (not used for now) */
    onRemovePlaylist() {
        const localRemText1 = this.translocoService.translate('general.removePlaylist2', {}, this.translocoService.getActiveLang());
        const localRemText2 = this.translocoService.translate('general.removeScenes', {}, this.translocoService.getActiveLang());
        if (!this.activeChannelPlaylist) {
            return this.openDialog(`${localRemText1}`, {});
        }
        const dialogRef = this.dialog.open(DialogPopComponent, {
            height: '333px',
            width: '400px',
            data: {name: `${localRemText2} ${this.activeChannelPlaylist.$.label}?`, showCancel: true}
        });
        this.cod(
            dialogRef.afterClosed().subscribe(result => {
                if (!result) return;
                const sceneIdToDelete = this.activeChannelPlaylistId;
                this.storeSelector.dispatch(actionSelectedSceneItemId({id: this.sceneId}));
                this.storeSelector.dispatch(actionRemoveSceneBlocks({
                        sceneId: this.sceneId,
                        sceneItemId: null,
                        values: [{
                            key: null,
                            value: sceneIdToDelete,
                            storePath: 'Data[0].Scene[0].Players[0].Player'
                        }]
                    })
                );
            })
        );
    }

    /* Toolbar action: remove players */
    onRemovePlayers() {
        const removePlayersAlert = this.translocoService.translate('general.sureRemove', {}, this.translocoService.getActiveLang());
        const dialogRef = this.dialog.open(DialogPopComponent, {
            height: '333px',
            width: '400px',
            data: {name: `${removePlayersAlert}`, showCancel: true}
        });
        this.cod(
            dialogRef.afterClosed().subscribe(result => {
                if (!result) return;
                this.removePlayers();
            })
        );
    }

    /* Toolbar action: copy players */
    onCopyPlayers() {
        this.setSelectedPlayers();
        this.copiedPlayers = _.cloneDeep(this.selectedPlayers);
    }

    /* Toolbar action: paste players
     * e: if e is false, then action is emit event from timeline right click
     */
    onPastePlayers(e) {
        const selectChannelAlert = 'Please select channel to paste players';
        if (!this.activeChannelPlaylistId) {
            return this.openDialog(`${selectChannelAlert}`, {});
        }

        const timelineState = this.state;
        if (!timelineState) return;
        const pages = reorderPages(timelineState.items, timelineState.channels);
        const selectedPlaylist = Object.values(pages).find((i_playlist) => i_playlist.playlistId == this.activeChannelPlaylistId);
        const playersToPaste = _.cloneDeep(this.copiedPlayers);

        if ('id' in selectedPlaylist) {
            const updatedPages = [];
            const currentPages: Array<any> = pages[`pages_${selectedPlaylist.id}`];
            Object.values(currentPages).forEach((i_page: IPlaylistItem) => {
                const pageUpdated = createPageData(i_page, this.storeSelector);
                updatedPages.push(pageUpdated);
            });
            playersToPaste.forEach((i_page: IPlaylistItem) => {
                const originalData = i_page.originalData;
                if (originalData && 'id' in originalData.Player[0].$) {
                    originalData.Player[0].$.id = _.uniqueId('__');
                }
                updatedPages.push(originalData);
            });

            this.dispatchSceneItemChanges({
                sceneId: this.sceneId,
                sceneItemId: selectedPlaylist.playlistId,
                values: [{
                    key: 'Page',
                    value: updatedPages,
                    storePath: this.storePath
                }]
            }, true);
        }
        if (this.enableUndoRedo) {
            this.storeSelector.dispatch({type: 'BREAK_MERGE'});
        }

        if (e == false) {
            this.storeSelector.dispatch(actionSelectedSceneItemId({id: this.activeChannelPlaylistId}));
        }
    }

    /* Toolbar action: cut players */
    onCutPlayers() {
        this.setSelectedPlayers();
        this.copiedPlayers = _.cloneDeep(this.selectedPlayers);
        this.removePlayers();
        // this.resetSelection();
    }

    /* Toolbar action: back to scene */
    onBackScene() {
        // this.backScene.emit();
        // this.appService.updateMultiScene([this.sceneId]);
        // this.storeSelector.dispatch(actionSelectedSceneItemId({ id: this.sceneId }));
        this.containerService.updateEditorMode(null);
    }

    /* Toolbar action:
     * Toggle Select all or Deselect playlist options
     */
    toggleSelectAll() {
        this.storeSelector.dispatch(actionSelectedSceneItemId({id: this.sceneId}));
        this.copiedPlayers = [];
        this.selectedPlayers = [];

        if (this.playlistSelection.length > 0) {
            this.playlistSelection = [];
        } else {
            this.playlistSelection = this.playlistOptions;
            this.render();
        }
    }

    /* Emit action: call when player of timeline is resized */
    onResizeItems(event) {
        this.setStorePages('Resize');
    }

    /* Emit action: call when player of timeline is clicked */
    itemsClicked(event) {
        // this.selectedPlayers = event.map((e) => _.pick(e, ['channel', 'originalData', 'pageId']));
        // this.cd.detectChanges();
    }

    /* Emit action: call when channel of timeline is clicked */
    onClickChannel(event) {
        if (!event) {
            return;
        }
        const playerId: Id = event.playlistId;
        if (this.activeChannelPlaylistId != playerId) {
            this.activeChannelPlaylistId = playerId;
            this.storeSelector.dispatch(actionSelectedSceneItemId({id: playerId}));
        }
        this.activeChannelPlaylist = this.sceneBlocks.find((v) => compareIds(v.$.id, playerId));
    }

    /* Emit action: call when player of timeline is moved */
    onMoveItems(event) {
        this.setStorePages('Move');
    }

    // icon click event: when user click "zoom-in" icon of toolbar
    onClickZoomIn() {
        if (this.zoomIndex > 0) {
            this.zoomIndex -= 1;
            this.zoom = TIMELINE_ZOOMS[this.zoomIndex];
            this.localStoreService.setStorageValConfig('timelineZoom', this.zoom);
        }
    }

    // icon click event: when user click "zoom-out" icon of toolbar
    onClickZoomOut() {
        if (this.zoomIndex < TIMELINE_ZOOMS.length - 1) {
            this.zoomIndex += 1;
            this.zoom = TIMELINE_ZOOMS[this.zoomIndex];
            this.localStoreService.setStorageValConfig('timelineZoom', this.zoom);
        }
    }

    onClickAlign(align) {
        this.setSelectedPlayers();
        if (this.selectedPlayers.length == 0) {
            return;
        }
        const n = this.selectedPlayers.length;
        const countItems = this.state.channels[this.selectedPlayers[0].channel].pageIndexes.length;
        const alignedIndexes = [];
        this.selectedPlayers.forEach((_player, i) => {
            if (align == 'left') {
                this.state.items[_player.pageId].start = -1 * (n - i) * 10;
                alignedIndexes.push(i);
            } else if (align == 'right') {
                this.state.items[_player.pageId].start = this.state.channels[_player.channel].duration + i * 10;
                alignedIndexes.push(countItems - i - 1);
            } else {
                this.state.items[_player.pageId].start = this.state.channels[_player.channel].duration / 2 + i * 10;
            }
        });

        this.setStorePages('Align');

        // set selected indexes after align done
        if (alignedIndexes.length > 0) {
            this.storeSelector.dispatch(actionSceneItemPlaylist({indexes: alignedIndexes}));
        }
    }

    // click event: when user click "Undo" icon of toolbar
    onClickUndo() {
        this.storeSelector.dispatch({type: 'UNDO'});
        this.resetSelection();
    }

    // click event: when user click "Redo" icon of toolbar
    onClickRedo() {
        this.storeSelector.dispatch({type: 'REDO'});
        this.resetSelection();
    }

    onKeyboardHelp() {
        this.dialog.open(DialogHotkeysComponent, {
            width: '450px',
            data: {
                mode: 'timeline'
            }
        });
    }

    jumpToPlayer(item: IPlaylistItem) {
        if (!item) return;

        // a component OR resource page embeds its player directly (no scene src). Edit it focused: the canvas
        // shows ONLY this player (the property panel stays in its normal place). Edits write back to the
        // nested player by id. The breadcrumb (Scene > ... > item) gives a way back.
        if (item.pageType === 'component' || item.pageType === 'resource') {
            const player = item?.originalData?.Player?.[0];
            const id = player?.$?.id;
            if (!id) return;

            // the parent playlist's region (its Layout on the scene) -> the focused canvas is sized to it, so
            // the item shows in the exact slot it occupies at runtime. A page player carries NO Layout of its
            // own (it fills the slot), so the region is the ONLY size there is - never write a Layout onto the
            // page here, that would freeze it at today's slot size and re-add a layout panel to edit.
            const channel = this.state?.channels?.find((c) => c.id == item.channel);
            const playlistBlock = channel ? this.sceneBlocks?.find((b) => compareIds(b.$.id, channel.playlistId)) : null;
            const region = playlistBlock?.Data?.[0]?.Layout?.[0]?.$;

            // a Group page is a CONTAINER, not a leaf component: drill into its children (groupEdit) exactly
            // like drilling into a group on the scene. Opening it as a single component would render the
            // "Group is empty" placeholder with the toolbox closed - no way to put anything inside it.
            if (isGroupItem(player)) {
                this.containerService.updateComponentEditTarget({
                    sceneId: this.sceneId,
                    componentId: id,
                    regionWidth: region?.width,
                    regionHeight: region?.height,
                    groupEdit: true,
                    needsToolbox: true,
                });
                this.containerService.updateEditorMode('componentMode');
                this.storeSelector.dispatch(actionSelectedSceneItemId({ id }));
                return;
            }

            // an aggregator/data-provider component (Playlist, Collection, JsonPlayer, ...) needs the toolbox
            // to be populated -> keep it open in the focused editor instead of hiding it
            const playerId = player?.$?.player;
            const needsToolbox = requiresToolboxContent(playerId ? this.storeSelector.getBlockLabelById(playerId) : null);
            // the breadcrumb (scene > playlist > component) is derived from the selection, no manual push needed
            this.containerService.updateComponentEditTarget({
                sceneId: this.sceneId,
                componentId: id,
                regionWidth: region?.width,
                regionHeight: region?.height,
                needsToolbox,
            });
            this.containerService.updateEditorMode('componentMode');
            this.storeSelector.dispatch(actionSelectedSceneItemId({ id }));
            return;
        }

        if (!item.pageSrc) return;

        if (item.pageType === 'scene') {
            this.storeSelector.dispatch(actionSceneJumpLinked({previousLinkedSceneId: this.sceneId}));
            this.containerService.updateEditorMode(null);

            // include the parent playlist in the trail -> "scene > playlist > target scene"
            const channel = this.state?.channels?.find((c) => c.id == item.channel);
            const playlistBlock = channel ? this.sceneBlocks?.find((b) => compareIds(b.$.id, channel.playlistId)) : null;
            const viaItem = playlistBlock ? { id: playlistBlock.$.id, label: playlistBlock.$.label || 'playlist' } : null;

            // Get current scene data for navigation
            this.sceneService.handleSceneBreadcrumbs(
                item.pageSrc,
                this.sceneId,
                this.sceneLabel,
                () => {
                    // drill in place - keep the root scene in the URL (see SceneService.enterNestedScene)
                    this.sceneService.enterNestedScene(item.pageSrc);
                },
                viaItem
            );
        }
    }

    expandPlaylist() {
        this.containerService.updateEditorMode('playlistMode');
    }

    toggleScenePreview() {
        this.scenePreviewEnabled = !this.scenePreviewEnabled;
        const val = convertBooleanToNumbers(this.scenePreviewEnabled, true);
        this.localStoreService.setStorageValConfig('playlistScenePreviewEnabled', val);
    }

    onMouseOver() {
        this.containerService.isMouseOnPlaylist.next(true);
    }

    onMouseOut() {
        this.containerService.isMouseOnPlaylist.next(false);
        this.removeDragPreview();
    }

    addItemToPlaylist(item) {
        if (!this.sceneItemId || this.sceneId == this.sceneItemId) {
            this.toastr.warning(this.translocoService.translate('toast.selectPlaylist', {}, this.translocoService.getActiveLang()));
            return;
        }
        ;

        const pages = this.getPagesOfPlaylist(this.sceneItemId);
        const newPage = this.generatePageForPlaylist(item);
        pages.push(newPage);

        this.dispatchSceneItemChanges({
            sceneId: this.sceneId,
            sceneItemId: this.sceneItemId,
            values: [{
                key: 'Page',
                value: pages,
                storePath: 'Data[0].Playlist[0]'
            }]
        }, true);
        if (this.enableUndoRedo) {
            this.storeSelector.dispatch({type: 'BREAK_MERGE'});
        }
    }

    dndItemToPlaylist(data) {
        // get drop point inside playlist timeline
        const {offsetX, offsetY} = this.getOffsetInsidePlaylist(data);
        if (isNullOrUndefined(offsetX) || isNullOrUndefined(offsetY)) {
            this.removeDragPreview();
            return;
        }
        ;

        const durationX = roundNumber(offsetX * this.zoom / TIMELINE_SCALE_LONG);

        // get playlist to drop based on offsetY
        const chIndex = Math.floor(Math.max(offsetY, 0) / TIMELINE_GRID_H2);
        if (!this.state.channels[chIndex]) return;
        const playlistId = this.state.channels[chIndex].playlistId;
        const defaultDuration = this.state.channels[chIndex].defaultDuration;

        // get pages of playlist
        const pages = this.getPagesOfPlaylist(playlistId);
        const {left, dropIndex} = this.getDropPosition(pages, defaultDuration, durationX);

        if (this.dragPreviewEle && data.isDragging) {
            this.dragPreviewEle.style.setProperty('left', `${left + this.rulerOffsetX}px`);
            this.dragPreviewEle.style.setProperty('top', `${chIndex * TIMELINE_GRID_H2 + this.rulerH}px`);
            this.dragPreviewEle.style.setProperty('height', `${TIMELINE_GRID_H2}px`);
            this.dragPreviewEle.style.setProperty('width', `${Math.round(defaultDuration * TIMELINE_SCALE_LONG / this.zoom / 2)}px`);
            this.dragPreviewEle.style.setProperty('border', '3px dashed red');
            this.cd.detectChanges();
            return;
        }

        const newPage = this.generatePageForPlaylist(data.item, playlistId);
        pages.splice(dropIndex, 0, newPage);

        this.dispatchSceneItemChanges({
            sceneId: this.sceneId,
            sceneItemId: playlistId,
            values: [{
                key: 'Page',
                value: pages,
                storePath: 'Data[0].Playlist[0]'
            }]
        }, true);

        if (this.enableUndoRedo) {
            this.storeSelector.dispatch({type: 'BREAK_MERGE'});
        }
        setTimeout(() => {
            this.removeDragPreview();
        }, 200);
    }

    backSceneList() {
        this.containerService.setMarkedTableRow(this.sceneId);
        this.router.navigateByUrl('/editor/list');
    }

    private getPagesOfPlaylist(playlistId) {
        if (!playlistId) return [];

        // top-level block, or a NESTED playlist (a Playlist embedded as a page inside another playlist) -
        // without the deep lookup a nested playlist returns [] here, so each add would OVERWRITE the
        // existing pages instead of appending
        const playlist = this.sceneBlocks.find((p) => p.$.id == playlistId) || this.findPlaylistDeep(playlistId);
        if (!playlist) return [];

        const pages = playlist.Data[0].Playlist[0].Page;
        return pages && _.isArray(pages) ? _.cloneDeep(pages) : [];
    }

    // the playlist's own box on the scene — the slot its pages render in
    private getPlaylistRegion(playlistId) {
        const playlist = this.sceneBlocks.find((p) => p.$.id == playlistId) || this.findPlaylistDeep(playlistId);
        return playlist?.Data?.[0]?.Layout?.[0]?.$;
    }

    private generatePageForPlaylist(item, playlistId = this.sceneItemId) {
        let newPage: IPage;
        const {blockId, blockType, blockName, blockTemplate} = item;

        if (blockType === 'component' && blockTemplate) {
            // embed the component directly in the playlist as its own page: clone the toolbox template
            // (config) and assign a fresh id, but DROP the Layout - a page fills its playlist slot, exactly
            // like a resource page (which is built with no Layout at all). SignLib's PlayerContainer forces
            // usePlayerLayoutX/Y=false on a page, so it is always 100% of the slot and follows the playlist
            // when it is resized. Keeping the template's Layout as a SIZE instead dropped a 100x100 postage
            // stamp (a Group's default) into the slot.
            //
            // A GROUP is the exception: it carries a DESIGN SIZE (Data[0].Group[0].$.width/height) - the canvas
            // its items are laid out on. Seed it to the slot the group is dropped into (so it starts out 1:1),
            // and SignLib scales design -> live slot from then on (GroupComponent.applyScale: scaleX =
            // actualWidth / designWidth). Without seeding it starts at the template's 100x100 and the items
            // render tiny in a larger slot. Its outer Layout is dropped like any page (a page fills its slot).
            const player: ISceneBlock = _.cloneDeep(blockTemplate);
            player.$.id = _.uniqueId('__');
            if (player?.Data?.[0]) {
                const region = this.getPlaylistRegion(playlistId);
                if (isGroupItem(player) && region?.width && region?.height) {
                    setGroupDesignBox(player, +region.width, +region.height);
                }
                delete player.Data[0].Layout;
            }
            newPage = {
                $: {
                    page: blockName || player?.$?.label || 'component',
                    type: "component"
                },
                Player: [player]
            };
            return newPage;
        }

        if (blockType === 'scene') {
            newPage = {
                $: {
                    page: blockName || 'new scene',
                    type: "scene",
                },
                Player: [
                    {
                        $: {
                            src: blockId
                        }
                    }
                ]
            };
        } else {
            const playerId = (["mp4", "mov", "m4v", "3gp", "webm"].indexOf(blockType) > -1) ? "3100" : "3130";
            newPage = {
                $: {
                    page: blockName || 'new resource',
                    type: "resource"
                },
                Player: [
                    {
                        "$": {
                            player: playerId,
                            id: _.uniqueId('__')
                        },
                        Data: [
                            {
                                Resource: [
                                    {
                                        $: {
                                            resource: blockId
                                        }
                                    }
                                ]
                            }
                        ]
                    }
                ]
            };
            if (playerId === '3100') {
                const res = this.resources.find((res) => compareIds(res.resource_id, blockId));
                if (res && res.resource_total_time && parseFloat(res.resource_total_time)) {
                    newPage.$.duration = res.resource_total_time;
                }
            }
        }
        return newPage;
    }

    private getOffsetInsidePlaylist(data) {
        if (!data) return {};

        const playlistEle = document.querySelector("#playlistContainer");  // should get element here, because whenever scroll, it will be different.
        if (playlistEle) {
            this.playlistBounds = playlistEle.getBoundingClientRect();
        } else {
            return {};
        }
        const offsetX = (data.dropPointX - this.playlistBounds.left);
        const offsetY = (data.dropPointY - this.playlistBounds.top);
        return {
            offsetX,
            offsetY
        };
    }

    private getDropPosition(pages, defaultDuration, durationX) {
        if (!pages || durationX <= 0) {
            return {
                left: 0,
                dropIndex: 0
            };
        }

        // get page index where to drop
        const pageLen = pages.length;
        let duration = 0;
        let newIndex = -1;
        let page: IPlaylistItem;

        pages.forEach((_page, ind) => {
            if (page) return;
            const d = _page.$.duration ? parseFloat(_page.$.duration) : defaultDuration;
            if (ind == pageLen - 1 || (duration < durationX && duration + d >= durationX)) {
                newIndex = ind;
                page = _page;
            }
            duration += d;
        });

        newIndex = Math.min(newIndex + 1, pageLen);
        return {
            left: Math.round(duration * TIMELINE_SCALE_LONG / this.zoom),
            dropIndex: newIndex
        };
    }

    private removeDragPreview() {
        if (!this.dragPreviewEle) return;
        this.dragPreviewEle.style.setProperty('left', 0);
        this.dragPreviewEle.style.setProperty('top', 0);
        this.dragPreviewEle.style.setProperty('height', 0);
        this.dragPreviewEle.style.setProperty('width', 0);
        this.dragPreviewEle.style.setProperty('border', 'none');
        this.cd.detectChanges();
    }

    private setSelectedPlayers() {
        if (!this.state || !this.selectedIndexes || this.selectedIndexes.length == 0 || !this.state.items) {
            this.selectedPlayers = [];
            return;
        }
        // Derive the selection directly from the store indexes + active channel rather than the per-item
        // `.selected` flag. That flag is mutated asynchronously by the child timeline, so on auto-select of
        // the first item the parent would read a stale flag and leave the drill-in button disabled until a
        // DIFFERENT item is picked. selectedIndexes are positions within the active channel.
        const activeChannel = this.getActiveChannel();
        if (activeChannel) {
            const selectedPageIds = this.selectedIndexes
                .map((idx) => activeChannel.pageIndexes[idx])
                .filter((pageId) => pageId != null);
            this.selectedPlayers = this.state.items.filter((_item) => selectedPageIds.indexOf(_item.pageId) > -1);
            return;
        }
        this.selectedPlayers = this.state.items.filter((_item) => _item.selected == true);
    }

    private resetSelection() {
        this.storeSelector.dispatch(actionSceneItemPlaylist({indexes: []}));
    }

    // -------------------------------------------------------------------------------------------------
    // batch item actions ("actions" dropdown) - selection + per-property setters applied only to the
    // items that support each property (aspect ratio: images + videos; audio: videos only)
    // -------------------------------------------------------------------------------------------------

    // the channel whose items the selection indexes refer to (selectedIndexes are positions within it)
    private getActiveChannel(): IPlaylistChannel {
        if (!this.state?.channels?.length) return null;
        return this.state.channels.find((c) => compareIds(c.playlistId, this.activeChannelPlaylistId))
            || this.state.channels.find((c) => c.selected)
            || this.state.channels[0];
    }

    // open the playlist pages as the roomy table dialog (the SAME dialog the property grid opens) -
    // block-playlist's table registers itself under this key via TableDialogService. If the property panel
    // hasn't been rendered yet (it is lazy - only mounts once expanded), requestOpen asks the scene editor
    // to expand it; the dialog then opens automatically as soon as the table registers. No toast needed -
    // this button only exists while a playlist is being edited, so the context is always valid.
    openPagesDialog() {
        this.tableDialogService.requestOpen('playlistPages');
    }

    selectAllItems() {
        const ch = this.getActiveChannel();
        if (!ch) return;
        this.storeSelector.dispatch(actionSceneItemPlaylist({indexes: ch.pageIndexes.map((_v, i) => i)}));
    }

    selectNoneItems() {
        this.storeSelector.dispatch(actionSceneItemPlaylist({indexes: []}));
    }

    reverseSelection() {
        const ch = this.getActiveChannel();
        if (!ch) return;
        const current = this.selectedIndexes || [];
        const indexes = ch.pageIndexes.map((_v, i) => i).filter((i) => current.indexOf(i) === -1);
        this.storeSelector.dispatch(actionSceneItemPlaylist({indexes}));
    }

    setSelectedMaintainRatio(on: boolean) {
        // aspect ratio applies to images AND videos
        this.applyToSelectedItems(
            (player) => this.isImageOrVideoPlayer(player),
            [{key: 'maintain', value: on ? '1' : '0', storePath: 'Data[0].Resource[0].AspectRatio[0].$'}]
        );
    }

    setSelectedVideoVolume(mute: boolean) {
        // audio applies to videos only
        this.applyToSelectedItems(
            (player) => this.isVideoPlayer(player),
            [{key: 'volume', value: mute ? '0' : '1', storePath: 'Data[0].Resource[0].Video[0].$'}]
        );
    }

    private isImageOrVideoPlayer(player): boolean {
        const p = String(player?.$?.player);
        return p === this.blockLabelRecords.IMAGE.id || p === this.blockLabelRecords.VIDEO.id;
    }

    private isVideoPlayer(player): boolean {
        return String(player?.$?.player) === this.blockLabelRecords.VIDEO.id;
    }

    // apply a property change to every selected item whose player matches the predicate (a page's player
    // is nested in the playlist Page[]; the store update finds it by id). Only the matching items change.
    private applyToSelectedItems(predicate: (player: any) => boolean, values: any[]) {
        this.setSelectedPlayers();
        if (this.selectedPlayers.length === 0) {
            this.toastr.warning(this.translocoService.translate('toast.selectPlaylist', {}, this.translocoService.getActiveLang()));
            return;
        }
        let applied = 0;
        this.selectedPlayers.forEach((item) => {
            const player = item?.originalData?.Player?.[0];
            if (!player?.$?.id || !predicate(player)) return;
            this.dispatchSceneItemChanges({sceneId: this.sceneId, sceneItemId: player.$.id, values}, true);
            applied++;
        });
        if (applied > 0 && this.enableUndoRedo) {
            this.storeSelector.dispatch({type: 'BREAK_MERGE'});
        }
    }

    private getResourceUrl(resId) {
        if (!this.resourceList?.[resId]) {
            const resource = this.resources.find((res) => compareIds(res.resource_id, resId));
            this.resourceList[resId] = resource;
        }
        if (!this.resourceList[resId]) return "";
        const resourcePreview: IResourcePreview = getResourcePreview(this.resourceList[resId], this.coreAppData, false, -1, false);
        return resourcePreview.url;
    }

    override destroy() {
        if (this.enableUndoRedo) {
            this.storeSelector.dispatch({type: 'BREAK_MERGE'});
        }
        if (!this.miniView) {
            this.storeSelector.dispatch(actionCRUDScene({
                data: null
            }));
        }
        this.containerService.setDndContent(null);
        // this timeline is the ONLY thing that sets isMouseOnPlaylist=true (onMouseOver). When it is torn
        // down (drill-in -> componentMode, back, scene switch) the pointer is definitionally no longer over
        // it, but a mouseleave never fires because the element is removed. Clear the flag so subsequent
        // canvas drops aren't misrouted into this (now-gone) playlist. See onMouseOut/onMouseOver.
        this.containerService.isMouseOnPlaylist.next(false);
    }
}
